import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ROUTES } from '../common/constants/urls';
import { getProjectSummary, getBudgetandLineItems, retrieveProjectDraws } from '../components/project/project.api';
import { createMagicReport, getMagicReport, resetMagicReport } from '../components/monitoring-reports/magic-report/magic-report.api';
import { handleError } from '../common/utils/util.errors';
import { DrawContext } from '../components/monitoring-reports/magic-report/magic-report.type';
import { DrawOption, drawSelectionReplacesReport } from '../components/project/MagicReportDrawSelectDialog';
import { getMissingRequiredFields, RequiredFieldGap } from '../components/project/project.util';
import { ProjectSummary } from '../components/project/project.type';

interface MagicReportProjectInfo {
  id: string;
  projectNumber: string;
  projectName: string;
}

/**
 * What the user is doing, which decides what confirming the dialog means.
 *
 * - `open` — continue working on the current period's report. Keeping the same draw set just
 *   reopens it; changing the set replaces the report.
 * - `next-report` — the period has rolled over. Always resets, pushing this period's values into
 *   "last month". The draw set is pre-checked to what the report covers, so confirming without
 *   touching it behaves exactly as the old no-choice flow did.
 */
export type MagicReportDrawSelectionMode = 'open' | 'next-report';

/**
 * Owns the "open magic report" and "start next report" flows for every entry point (project
 * footer, project table row).
 *
 * Both go through one dialog so that a multi-draw report loads every covered draw. The reset path
 * previously navigated with a single draw's data and no `draws` array, which silently collapsed a
 * 3-payapp report to 1 payapp in the wizard.
 */
export function useMagicReportDrawSelection() {
  const navigate = useNavigate();
  const [isPreparing, setIsPreparing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draws, setDraws] = useState<DrawOption[]>([]);
  const [project, setProject] = useState<MagicReportProjectInfo | null>(null);
  const [existingDrawIds, setExistingDrawIds] = useState<string[]>([]);
  const [existingHiddenCoLienDrawIds, setExistingHiddenCoLienDrawIds] = useState<string[]>([]);
  const [existingReportId, setExistingReportId] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<MagicReportDrawSelectionMode>('open');

  // Project title, number, observer, and bank name can never legitimately be missing from a
  // finished report.
  const [requiredFieldsGap, setRequiredFieldsGap] = useState<RequiredFieldGap[]>([]);
  const [requiredFieldsGapProjectId, setRequiredFieldsGapProjectId] = useState<string | null>(null);
  const [requiredFieldsGapProjectSummary, setRequiredFieldsGapProjectSummary] = useState<ProjectSummary | null>(null);
  const [pendingRetry, setPendingRetry] = useState<(() => void) | null>(null);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setProject(null);
    setDraws([]);
    setExistingDrawIds([]);
    setExistingHiddenCoLienDrawIds([]);
    setExistingReportId(undefined);
    setMode('open');
  }, []);

  const closeRequiredFieldsGap = useCallback(() => {
    setRequiredFieldsGap([]);
    setRequiredFieldsGapProjectId(null);
    setRequiredFieldsGapProjectSummary(null);
    setPendingRetry(null);
  }, []);

  /** Loads the project's draws and the existing report's draw set, then opens the dialog. */
  const openDrawSelection = useCallback(async (projectId: string, selectionMode: MagicReportDrawSelectionMode = 'open') => {
    if (!projectId) return;
    setIsPreparing(true);
    try {
      const [drawsResponse, projectSummary] = await Promise.all([retrieveProjectDraws(projectId), getProjectSummary(projectId)]);

      const requiredGap = getMissingRequiredFields(projectSummary);
      if (requiredGap.length > 0) {
        setRequiredFieldsGap(requiredGap);
        setRequiredFieldsGapProjectId(projectId);
        setRequiredFieldsGapProjectSummary(projectSummary);
        setPendingRetry(() => () => openDrawSelection(projectId, selectionMode));
        return;
      }

      const available = (((drawsResponse as any)?.draws || []) as DrawOption[]).filter(d => d.isEnabled !== false);
      if (available.length === 0) {
        toast.error('Draw does not exist for this project.');
        return;
      }

      let reportDrawIds: string[] = [];
      let reportHiddenCoLienDrawIds: string[] = [];
      const magicReportId = projectSummary?.magicReportId;
      if (magicReportId) {
        try {
          const report = await getMagicReport(magicReportId);
          reportDrawIds = ((report?.drawIds || []) as string[]).filter(id => available.some(d => d.id === id));
          reportHiddenCoLienDrawIds = ((report?.hiddenCoLienDrawIds || []) as string[]).filter(id => reportDrawIds.includes(id));
        } catch {
          // A missing or unreadable report just means nothing to pre-check.
          reportDrawIds = [];
          reportHiddenCoLienDrawIds = [];
        }
      }

      setProject({
        id: projectId,
        projectNumber: (drawsResponse as any)?.projectNumber ?? projectSummary?.projectNumber ?? '',
        projectName: (drawsResponse as any)?.projectTitle ?? projectSummary?.projectTitle ?? '',
      });
      setDraws(available);
      setExistingDrawIds(reportDrawIds);
      setExistingHiddenCoLienDrawIds(reportHiddenCoLienDrawIds);
      setExistingReportId(magicReportId);
      setMode(selectionMode);
      setDialogOpen(true);
    } catch (error) {
      handleError(error);
    } finally {
      setIsPreparing(false);
    }
  }, []);

  /**
   * Resolves the report for the chosen draws, loads each draw's budget and line items, and
   * navigates into the wizard.
   */
  const confirmDrawSelection = useCallback(
    async (drawIds: string[], hiddenCoLienDrawIds: string[] = [], options?: { onConfirmReplace?: () => Promise<boolean> }) => {
      if (!project || drawIds.length === 0) return;
      setIsOpening(true);
      try {
        const projectSummary = await getProjectSummary(project.id);
        if (!projectSummary) {
          toast.error('Project not found.');
          return;
        }

        const requiredGap = getMissingRequiredFields(projectSummary);
        if (requiredGap.length > 0) {
          setRequiredFieldsGap(requiredGap);
          setRequiredFieldsGapProjectId(project.id);
          setRequiredFieldsGapProjectSummary(projectSummary);
          setPendingRetry(() => () => confirmDrawSelection(drawIds, hiddenCoLienDrawIds, options));
          closeDialog();
          return;
        }

        const drawName = draws.find(d => d.id === drawIds[drawIds.length - 1])?.name || 'Draw';
        const reportName = `Magic Report: ${project.projectName} - ${drawName}`;

        let report: any;
        if (!existingReportId) {
          report = await createMagicReport({ name: reportName, projectId: projectSummary.projectId, drawIds, hiddenCoLienDrawIds });
        } else if (mode === 'next-report') {
          // Starting the next report always rolls the period over, whether or not the draw set
          // changed. Draws dropped from the set lose their carried-forward comments; the previous
          // report keeps them, since reset archives rather than deletes it.
          const confirmed = options?.onConfirmReplace ? await options.onConfirmReplace() : true;
          if (!confirmed) return;
          report = await resetMagicReport(existingReportId, projectSummary.projectId, reportName, drawIds, hiddenCoLienDrawIds);
        } else if (!drawSelectionReplacesReport(drawIds, existingDrawIds, true)) {
          // Draw set effectively unchanged: just reopen. Calling create here would delegate to
          // reset on the backend and destroy the report the user was coming back to.
          report = await getMagicReport(existingReportId);
        } else {
          const confirmed = options?.onConfirmReplace ? await options.onConfirmReplace() : true;
          if (!confirmed) return;
          report = await resetMagicReport(existingReportId, projectSummary.projectId, reportName, drawIds, hiddenCoLienDrawIds);
        }

        if (!report?.id) {
          toast.error('Failed to open report.');
          return;
        }

        // Load every covered draw in parallel, keeping the order the dialog produced.
        const drawData = await Promise.all(drawIds.map(drawId => getBudgetandLineItems(project.id, drawId)));
        const drawContexts: DrawContext[] = drawData
          .filter(data => data?.projectDrawSummary)
          .map(data => ({
            drawId: data.projectDrawSummary.id,
            drawSummary: data.projectDrawSummary,
            budgetSummary: data.budgetSummary,
            lineItems: data.lineItems,
          }));

        if (drawContexts.length === 0) {
          toast.error('Could not load budget data for the selected draws.');
          return;
        }

        // The primary draw (last = latest application) still drives the sections that have not
        // been migrated to multi-draw, so mirror it into the legacy single-draw fields.
        const primaryDraw = drawContexts[drawContexts.length - 1];

        const reportData = {
          report,
          projectSummary,
          draws: drawContexts,
          drawSummary: primaryDraw.drawSummary,
          budgetSummary: primaryDraw.budgetSummary,
          lineItems: primaryDraw.lineItems,
          summary: { coverPageContent: report?.summary?.coverLetter },
        };

        navigate(`${ROUTES.PROJECTS}/${projectSummary.projectId}${ROUTES.MAGIC_REPORT}/${report.id}/upload-images`, {
          state: { reportData },
        });
        closeDialog();
      } catch (error) {
        handleError(error);
      } finally {
        setIsOpening(false);
      }
    },
    [project, draws, existingReportId, existingDrawIds, mode, navigate, closeDialog],
  );

  const retryAfterFieldsFixed = useCallback(() => {
    const retry = pendingRetry;
    closeRequiredFieldsGap();
    retry?.();
  }, [pendingRetry, closeRequiredFieldsGap]);

  return {
    openDrawSelection,
    confirmDrawSelection,
    closeDialog,
    dialogOpen,
    draws,
    project,
    existingDrawIds,
    existingHiddenCoLienDrawIds,
    hasExistingReport: Boolean(existingReportId),
    isPreparing,
    isOpening,
    requiredFieldsGap,
    requiredFieldsGapProjectId,
    requiredFieldsGapProjectSummary,
    requiredFieldsGapOpen: requiredFieldsGap.length > 0,
    retryAfterFieldsFixed,
    closeRequiredFieldsGap,
    mode,
  };
}
